import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const inputClass =
    "w-full border border-ivory/15 bg-night/40 px-4 py-3.5 text-sm text-ivory outline-none transition-colors placeholder:text-ivory/35 focus:border-champagne";

const formatError = (detail, fallback) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
    if (detail && typeof detail === "object") return Object.values(detail).join(" ");
    return fallback;
};

const post = async (path, body) => {
    const res = await fetch(`${API}/api/candidate/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatError(data.detail, "Something went wrong. Please try again."));
    return data;
};

export const AuthShell = ({ title, subtitle, children, footer }) => (
    <div className="flex min-h-screen items-center justify-center bg-night px-6 py-16 text-ivory">
        <div className="w-full max-w-md">
            <a href="/" data-testid="auth-home-link" className="mb-10 flex items-center gap-3.5">
                <span className="flex h-12 w-12 items-center justify-center bg-forest font-serif text-sm font-semibold tracking-[0.12em] text-ivory">
                    AGL
                </span>
                <span className="leading-tight">
                    <span className="block font-serif text-lg font-semibold uppercase tracking-[0.14em]">
                        Afrinexa Global
                    </span>
                    <span className="block text-[9px] font-medium uppercase tracking-[0.32em] text-ivory/50">
                        Candidate Portal
                    </span>
                </span>
            </a>
            <div className="border border-ivory/15 bg-ivory/[0.04] p-8 sm:p-10">
                <h1 className="font-serif text-2xl font-medium tracking-tight">{title}</h1>
                <p className="mt-2 text-sm text-ivory/60">{subtitle}</p>
                <div className="mt-8">{children}</div>
            </div>
            {footer && <div className="mt-8 text-center text-sm text-ivory/55">{footer}</div>}
        </div>
    </div>
);

const Field = ({ label, htmlFor, children }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ivory/60">
            {label}
        </label>
        {children}
    </div>
);

const SubmitButton = ({ busy, label, testid }) => (
    <button
        type="submit"
        data-testid={testid}
        disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-colors duration-300 hover:bg-forest-soft disabled:cursor-not-allowed disabled:opacity-80"
    >
        {busy && <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />}
        {busy ? "Please wait…" : label}
    </button>
);

const ErrorNote = ({ error, testid }) =>
    error ? (
        <p data-testid={testid} className="border-l-2 border-champagne pl-4 text-sm text-ivory/75">
            {error}
        </p>
    ) : null;

export default function CandidateLogin() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await post("login", form);
            navigate("/account");
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <AuthShell
            title="Welcome back"
            subtitle="Sign in to your candidate account."
            footer={
                <>
                    New to Afrinexa?{" "}
                    <a href="/account/register" data-testid="login-register-link" className="text-champagne hover:underline">
                        Create an account
                    </a>
                </>
            }
        >
            <form data-testid="candidate-login-form" onSubmit={submit} className="space-y-5">
                <Field label="Email" htmlFor="cl-email">
                    <input id="cl-email" data-testid="candidate-login-email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="you@example.com" />
                </Field>
                <Field label="Password" htmlFor="cl-password">
                    <input id="cl-password" data-testid="candidate-login-password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} placeholder="••••••••" />
                </Field>
                <div className="text-right">
                    <a href="/account/forgot-password" data-testid="forgot-password-link" className="text-xs text-ivory/55 transition-colors hover:text-champagne">
                        Forgot your password?
                    </a>
                </div>
                <ErrorNote error={error} testid="candidate-login-error" />
                <SubmitButton busy={busy} label="Sign In" testid="candidate-login-button" />
            </form>
        </AuthShell>
    );
}

export function CandidateRegister() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", country: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await post("register", form);
            navigate("/account");
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <AuthShell
            title="Create your account"
            subtitle="Apply for international roles, track your applications and manage your documents in one place."
            footer={
                <>
                    Already have an account?{" "}
                    <a href="/account/login" data-testid="register-login-link" className="text-champagne hover:underline">
                        Sign in
                    </a>
                </>
            }
        >
            <form data-testid="candidate-register-form" onSubmit={submit} className="space-y-5">
                <Field label="Full Name" htmlFor="cr-name">
                    <input id="cr-name" data-testid="register-name" type="text" required value={form.full_name} onChange={set("full_name")} className={inputClass} placeholder="Your full name" />
                </Field>
                <Field label="Email" htmlFor="cr-email">
                    <input id="cr-email" data-testid="register-email" type="email" required autoComplete="username" value={form.email} onChange={set("email")} className={inputClass} placeholder="you@example.com" />
                </Field>
                <Field label="Password (min. 8 characters)" htmlFor="cr-password">
                    <input id="cr-password" data-testid="register-password" type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")} className={inputClass} placeholder="Choose a strong password" />
                </Field>
                <Field label="Phone / WhatsApp" htmlFor="cr-phone">
                    <input id="cr-phone" data-testid="register-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+234 ..." />
                </Field>
                <Field label="Country of Residence" htmlFor="cr-country">
                    <input id="cr-country" data-testid="register-country" type="text" value={form.country} onChange={set("country")} className={inputClass} placeholder="e.g. Nigeria" />
                </Field>
                <ErrorNote error={error} testid="register-error" />
                <SubmitButton busy={busy} label="Create Account" testid="register-submit-button" />
            </form>
        </AuthShell>
    );
}

export function CandidateForgotPassword() {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await post("forgot-password", { email });
        } catch {
            // same confirmation either way
        }
        setDone(true);
    };

    return (
        <AuthShell title="Reset your password" subtitle="Enter your account email and we'll send you a reset link.">
            {done ? (
                <div data-testid="forgot-success" className="space-y-5">
                    <p className="text-sm leading-relaxed text-ivory/75">
                        If an account exists for that email, a password reset link is on its way. The link
                        expires in 30 minutes.
                    </p>
                    <a href="/account/login" data-testid="forgot-back-login" className="inline-block text-sm text-champagne hover:underline">
                        Back to sign in
                    </a>
                </div>
            ) : (
                <form data-testid="forgot-form" onSubmit={submit} className="space-y-5">
                    <Field label="Email" htmlFor="fp-email">
                        <input id="fp-email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="you@example.com" />
                    </Field>
                    <SubmitButton busy={busy} label="Send Reset Link" testid="forgot-submit-button" />
                </form>
            )}
        </AuthShell>
    );
}

export function CandidateResetPassword() {
    const navigate = useNavigate();
    const token = new URLSearchParams(window.location.search).get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            await post("reset-password", { token, password });
            setDone(true);
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <AuthShell title="Choose a new password" subtitle="Your new password must be at least 8 characters.">
            {done ? (
                <div data-testid="reset-success" className="space-y-5">
                    <p className="text-sm leading-relaxed text-ivory/75">
                        Your password has been updated. You can now sign in with your new password.
                    </p>
                    <button
                        type="button"
                        data-testid="reset-go-login"
                        onClick={() => navigate("/account/login")}
                        className="inline-flex items-center justify-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-forest-soft"
                    >
                        Sign In
                    </button>
                </div>
            ) : (
                <form data-testid="reset-form" onSubmit={submit} className="space-y-5">
                    <Field label="New Password" htmlFor="rp-password">
                        <input id="rp-password" data-testid="reset-password" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="New password" />
                    </Field>
                    <Field label="Confirm Password" htmlFor="rp-confirm">
                        <input id="rp-confirm" data-testid="reset-confirm" type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass} placeholder="Repeat new password" />
                    </Field>
                    <ErrorNote error={error} testid="reset-error" />
                    <SubmitButton busy={busy} label="Update Password" testid="reset-submit-button" />
                </form>
            )}
        </AuthShell>
    );
}
